import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import {
  IDividendGross,
  IHistoryStock,
  useDeleteDividendMutation,
  useDividendCreatedSubscription,
  useDividendDeletedSubscription,
  useDividendUpdatedSubscription,
  useGetClosePricesQuery,
  useGetDividendsGrossQuery
} from '../../../../generated/graphql';
import { exportDataAsExcel } from '../../../../utils/fileUtiles';
import { numberWithCommas } from '../../../../utils/mathUtils';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import InnerLoader from '../../../Common/InnerLoader';
import DenseTable from '../../../Common/Tables/DenseTable';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserCreate } from '../../../../utils/rightUtils';
import { renderDate } from '../../../../utils/styleUtils';
import DividendGrossForm from './DividendGrossForm';

interface IProps {
  stockId: string;
  stockName: string;
}

const DividendGrossTab: React.FunctionComponent<IProps> = ({ stockId, stockName }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [date, setDate] = useState<Date | null>(null);
  const [allData, setAllData] = useState([]);
  const [fileteredData, setFileteredData] = useState([]);
  const [openDividendForm, setOpenDividendForm] = useState(false);
  const [dividend, setDividend] = useState<IDividendGross | null>(null);
  const canCreate = canUserCreate(state.user, Types.Stock);
  const { data, loading } = useGetDividendsGrossQuery({
    fetchPolicy: 'no-cache',
    variables: {
      stock_id: parseInt(stockId as string)
    }
  });
  const [deleteDividend] = useDeleteDividendMutation();
  const createDividendSubscription = useDividendCreatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const updateDividendSubscription = useDividendUpdatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const deleteDividendSubscription = useDividendDeletedSubscription({
    variables: { stock_id: stockId || '' }
  });

  useEffect(() => {
    if (data && data.result) {
      const parsedData = data.result.map((n) => {
        const date = new Date(parseInt(n?.start_date as string));
        date.setHours(12);
        return {
          ...n,
          filter_date: date.toISOString().slice(0, 10)
        };
      }) as never[];
      setAllData(parsedData as never[]);
      setFileteredData(parsedData as never[]);
    }
  }, [data?.result]);

  useEffect(() => {
    if (createDividendSubscription.data?.result) {
      addDividend(createDividendSubscription.data.result);
    }
    if (deleteDividendSubscription.data?.result) {
      removeDividend(deleteDividendSubscription.data.result);
    }
    if (updateDividendSubscription.data?.result) {
      updateDividend(updateDividendSubscription.data?.result);
    }
  }, [
    createDividendSubscription.data,
    deleteDividendSubscription.data,
    updateDividendSubscription.data
  ]);

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 82
    },
    {
      title: 'Date Day',
      field: 'start_date',
      width: 167,
      type: 'date',
      render: (rowData: IDividendGross) => renderDate(rowData.start_date)
    },
    {
      title: 'Dividend Gross',
      field: 'value',
      width: 1321
    },
    {
      title: 'Remove',
      sorting: false,
      field: 'status.name',
      width: 71
    }
  ] as IHeadCell[];

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        // hack to prevent changing day due to time zones and using isoString
        input.setHours(12);
        const isoInput = input.toISOString().slice(0, 10);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setFileteredData(allData.filter((n) => moment(isoInput).isSame(n.filter_date)));
      }
    } else {
      setFileteredData(allData);
    }
    setDate(input);
  };

  const exportCurrentDividends = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const fileName = `${stockName.replaceAll(' ', '-')}-dividend-gross-${
      date ? `${moment(date).format('YYYY_MM_DD')}` : `${moment(new Date()).format('YYYY_MM_DD')}`
    }`;
    const csvData = (fileteredData as IDividendGross[]).map((d) => ({
      'Date Day': moment(parseInt(d.start_date)).format('DD-MM-yy'),
      'Dividend Gross': d.value
    }));
    exportDataAsExcel(csvData, fileName);
  };

  const handleClose = (saved: boolean, isNew?: boolean, dividend?: IDividendGross) => {
    if (saved && dividend) {
      if (isNew) {
        addDividend(dividend);
      } else {
        // updateNav(nav);
      }
    }

    setDividend(null);
    setOpenDividendForm(false);
  };

  const handleDetails = (rowData: IDividendGross) => {
    setDividend(rowData);
    setOpenDividendForm(true);
  };

  const addDividend = (dividend: IDividendGross) => {
    const dividendDate = new Date(parseInt(dividend?.start_date as string));
    dividendDate.setHours(12);
    const dividendInput = {
      ...dividend,
      filter_date: dividendDate.toISOString().slice(0, 10)
    } as never;

    setAllData([dividendInput, ...allData]);
    if (date) {
      const dividendDate = new Date(parseInt(dividend.start_date)).toISOString().slice(0, 10);
      const isoDate = date.toISOString().slice(0, 10);
      if (moment(isoDate).isSame(dividendDate)) {
        setFileteredData([dividendInput, ...fileteredData]);
      }
    } else {
      setFileteredData([dividendInput, ...fileteredData]);
    }
  };

  const updateDividend = (dividend: IDividendGross) => {
    const dividendDate = new Date(parseInt(dividend?.start_date as string));
    dividendDate.setHours(12);
    const updatedDividend = {
      ...dividend,
      filter_date: dividendDate.toISOString().slice(0, 10)
    } as never;
    const updated = allData.map((d) =>
      (d as IDividendGross).id === dividend.id ? (updatedDividend as never) : d
    );
    setAllData(updated);
    if (date) {
      const isoDate = date.toISOString().slice(0, 10);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setFileteredData(updated.filter((n) => moment(isoDate).isSame(n.filter_date)));
    } else {
      setFileteredData(updated);
    }
  };
  const handleRemove = (rowData: IDividendGross) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete dividend from ${renderDate(rowData.start_date)}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (dividend: IDividendGross) => {
    dispatch({ type: Actions.HideConfirmation });
    deleteDividend({
      variables: {
        id: dividend.id
      },
      update() {
        removeDividend(dividend);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `Dividend from ${renderDate(dividend.start_date)} deleted`
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const removeDividend = (dividend: IDividendGross) => {
    setAllData(allData.filter((d) => (d as IDividendGross).id !== dividend.id));
    setFileteredData(fileteredData.filter((d) => (d as IDividendGross).id !== dividend.id));
  };
  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <Box sx={{ position: 'absolute', pl: '25px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="divident-gross-date-picker"
                label="Date"
                value={date}
                onChange={handleDateChange}
              />
            </LocalizationProvider>
          </Box>
          <DenseTable
            columns={columns}
            data={fileteredData}
            handleDetails={handleDetails}
            handleRemove={handleRemove}
            createButtonProps={{
              id: 'create-dividend-button',
              onClick: () => setOpenDividendForm(true),
              text: 'Create',
              disabled: !canCreate
            }}
            exportButtonProps={{
              id: 'export-divident-gross-button',
              onClick: exportCurrentDividends
            }}
            maxHeight={1050}
            pageSize={15}
          />
          {openDividendForm && (
            <DividendGrossForm
              stockId={stockId as string}
              handleClose={handleClose}
              dividendInput={dividend}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default DividendGrossTab;
